import React, { ReactElement } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";

import { Client, User } from "resourceModels";
import { getResource } from "selectors/resources";
import { updateOne } from "actions/resources";

import {
  activateTFA as activateTFAAction,
  checkTFA as checkTFAAction,
  loginUser as loginUserAction
} from "actions";
import { Dispatch } from "actions/types";
import { State } from "reducers/types";
import { SessionRecord } from "reducers/session/reducer";
import { LoginRecord } from "source/components/Pages/Login/reducer";
import {
  resetNotificationError as ResetErrorNotificationAction,
  setCurrentStep as SetCurrentStepAction,
  setPreviousStep as SetPreviousStepAction
} from "../actions";
import { MFAPhoneNumberForm } from "./MFAPhoneNumberForm";
import { MFAVerificationForm } from "./MFAVerificationForm";
import MFAIntro from "./MFAIntro";

import MFAConfirmation from "./MFAConfirmation";

interface PropsFromState {
  client: Client;
  user: User;
  login: LoginRecord;
  session: SessionRecord;
}

interface PropsFromDispatch {
  activateTFA(
    countryCode: string,
    phoneNumber: string,
    userId: string,
    calledFromEnforceMFA?: boolean
  ): void;
  checkTFA(code: string, loginCallback: boolean, calledFromEnforceMFA?: boolean): void;
  loginUser(email: string, password: string): void;
  resetNotificationError(): void;
  setCurrentStep(): void;
  setPreviousStep(): void;
  updateUser(id: string, attributes: Record<string, unknown>): void;
}

type EnforcedMfaProps = PropsFromState & PropsFromDispatch;

export class EnforcedMfa extends React.Component<EnforcedMfaProps> {
  state = {
    phoneNumber: "",
    verificationCode: "",
    countryCode: ""
  };

  constructor(props: EnforcedMfaProps) {
    super(props);

    this.handleInputChange = this.handleInputChange.bind(this);
    this.onStepChange = this.onStepChange.bind(this);
    this.activateTFA = this.activateTFA.bind(this);
    this.handleCloseNotification = this.handleCloseNotification.bind(this);
    this.handleSetPreviousStep = this.handleSetPreviousStep.bind(this);
    this.checkTFA = this.checkTFA.bind(this);
    this.completeMFASetup = this.completeMFASetup.bind(this);
  }

  /** Sets state on current step of enforced MFA flow */
  onStepChange(): void {
    const { setCurrentStep } = this.props;
    setCurrentStep();
  }

  handleCloseNotification = (): void => {
    const { resetNotificationError } = this.props;
    resetNotificationError();
  };

  /** Sets state on active input */
  handleInputChange(field: string, value: string): void {
    this.setState({
      [field]: value
    });
  }

  /** Check that the verification code sent by authy is correct.
   * Verify that the user inputs the correct phone number */
  checkTFA(): void {
    const { checkTFA } = this.props;
    const { verificationCode } = this.state;
    checkTFA(verificationCode, false, true);
  }

  /** Step one in enable authy flow. Send userId, country code and phone number to authy
   * to trigger SMS with authy verification code */
  activateTFA(): void {
    const { activateTFA, user } = this.props;
    const { countryCode, phoneNumber } = this.state;
    activateTFA(
      countryCode,
      phoneNumber,
      user.id,
      true
    );
  }

  handleSetPreviousStep(): void {
    const { setPreviousStep } = this.props;
    setPreviousStep();
  }

  /**
   * after successfully enabling authy redirect users into app and
   * update authyEnabled field on user model so changes updated in app settings
   */
  completeMFASetup(): void {
    const {
      user, updateUser, login, loginUser
    } = this.props;
    const { email, password } = login;

    updateUser(user.id, {
      authyEnabled: true
    });

    loginUser(email, password);
  }

  render(): ReactElement {
    const {
      phoneNumber, verificationCode, countryCode
    } = this.state;
    const {
      client, user, session, login
    } = this.props;

    return (
      <div className="EnforcedMfa">
        <MFAIntro
          currentStep={login.currentStep}
          clientName={client.name}
          onClick={this.onStepChange}
        />

        <MFAPhoneNumberForm
          currentStep={login.currentStep}
          onClick={this.activateTFA}
          handleInputChange={this.handleInputChange}
          phoneNumber={phoneNumber}
          countryCode={countryCode}
          buttonIsLoading={session.isAuthenticating}
          buttonIsDisabled={!phoneNumber || !countryCode}
          showError={session.hasAuthenticationError}
          resetNotificationError={this.handleCloseNotification}
          errorMessage={session.errorMessage}
          user={user}
        />

        <MFAVerificationForm
          currentStep={login.currentStep}
          countryCode={countryCode}
          phoneNumber={phoneNumber}
          handleWrongNumber={this.handleSetPreviousStep}
          onClick={this.checkTFA}
          handleInputChange={this.handleInputChange}
          verificationCode={verificationCode}
          // TODO: buttonIsDisabled is not being used. Either use it or remove this prop.
          buttonIsDisabled={!verificationCode}
          showError={session.hasAuthenticationError}
          resetNotificationError={this.handleCloseNotification}
          errorMessage={session.errorMessage}
        />

        <MFAConfirmation
          currentStep={login.currentStep}
          onClick={this.completeMFASetup}
        />
      </div>
    );
  }
}

function mapState(state: State): PropsFromState {
  const {
    login, session
  } = state;

  return {
    client: getResource(state, "client"),
    user: getResource(state, "user"),
    login,
    session
  };
}

function mapDispatch(dispatch: Dispatch): PropsFromDispatch {
  return {
    activateTFA: bindActionCreators(activateTFAAction, dispatch),
    checkTFA: bindActionCreators(checkTFAAction, dispatch),
    loginUser: bindActionCreators(loginUserAction, dispatch),
    resetNotificationError: bindActionCreators(ResetErrorNotificationAction, dispatch),
    setCurrentStep: bindActionCreators(SetCurrentStepAction, dispatch),
    setPreviousStep: bindActionCreators(SetPreviousStepAction, dispatch),
    updateUser: (id, data) => dispatch(updateOne("user", id, data))
  };
}

const Connector = connect(mapState, mapDispatch)(EnforcedMfa);

export default Connector;
